import { useState, useEffect } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three/webgpu'

// Bakes a top-down land mask, then converts it to a distance field for shoreline foam.
//
// This is the Unreal-style approach: render the terrain ONCE from directly above with an
// orthographic camera, then flood-fill outwards from the coastline so every texel stores how far
// it is from land, in world units. The water shader samples it by world XZ.
//
// Why not screen-space depth sampling: that inherits the land's on-screen silhouette, so the foam
// band is clipped by cliff geometry and reads as jagged triangle edges. A world-space distance
// field has no such dependency — the band is smooth regardless of viewing angle, and its width is
// a real distance rather than a pixel radius.
//
// Baked once on mount. The terrain is static, so there is no per-frame cost.
// Pre-baked field shipped in public/. Generated by loading the site with ?bakeshoreline=true,
// which downloads the PNG plus its metadata — see "Re-baking" at the bottom of this file.
// ?bakeshoreline=true re-runs the bake at runtime and downloads the result.
const FORCE_BAKE = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('bakeshoreline') === 'true'

// ONE baked field, shared by desktop and mobile. The coastline is the same shape in both GLBs
// (they differ in texture resolution, not geometry), so a single mask is correct for both.
const BAKED_URL = '/textures/shoreline-mask.png'
const BAKED_META_URL = '/textures/shoreline-mask.json'

export function useShorelineMask({
  size = 2000,      // world units covered by the bake, centred on the origin
  resolution = 512, // texels per side
  waterLevel = 0.5, // world Y of the water plane; anything above this counts as land
  maxDistance = 40, // world units of falloff encoded into the field
} = {}) {
  const gl = useThree(state => state.gl)
  const scene = useThree(state => state.scene)
  const [mask, setMask] = useState(null)

  // Load the pre-baked field. This is the normal path: no render target, no readback, no
  // distance transform, and no 1.5s wait before the ocean can appear.
  useEffect(() => {
    if (FORCE_BAKE) return
    let cancelled = false

    Promise.all([
      fetch(BAKED_META_URL).then(r => r.ok ? r.json() : Promise.reject(new Error('no meta'))),
      new Promise((resolve, reject) => {
        new THREE.TextureLoader().load(BAKED_URL, resolve, undefined, reject)
      }),
    ])
      .then(([meta, texture]) => {
        if (cancelled) return
        texture.wrapS = THREE.ClampToEdgeWrapping
        texture.wrapT = THREE.ClampToEdgeWrapping
        texture.minFilter = THREE.LinearFilter
        texture.magFilter = THREE.LinearFilter
        texture.generateMipmaps = false
        // The PNG is authored data, not colour — it must not be sRGB-decoded.
        texture.colorSpace = THREE.NoColorSpace
        // flipY stays at TextureLoader's default (true). Confirmed by eye: this orientation is
        // the one that aligns the foam with the coastline. Do not "correct" it without testing —
        // the bake, the canvas export and the sampler each have their own row convention, and
        // reasoning about them in the abstract got this wrong twice.
        texture.flipY = true
        setMask({
          texture,
          size: meta.size,
          centre: meta.centre,
          maxDistance: meta.maxDistance,
        })
      })
      .catch((err) => {
        // No silent runtime fallback: the baked field is a shipped asset, and quietly baking it
        // on the client would hide a missing file behind a slow load. Foam is simply absent, and
        // the reason is stated once. Regenerate with ?bakeshoreline=true (see below).
        if (!cancelled) console.warn(
          `[shoreline] ${BAKED_URL} missing or failed to load — shoreline foam disabled. ` +
          `Re-bake with ?bakeshoreline=true and place the files in public/textures/.`, err,
        )
      })

    return () => { cancelled = true }
  }, [])

  // AUTHORING ONLY — never runs in normal use.
  //
  // Generates the distance field and downloads it. Gated behind ?bakeshoreline=true so a normal
  // page load does no render-target work, no GPU readback and no distance transform.
  useEffect(() => {
    if (!FORCE_BAKE) return
    if (!gl?.isWebGPURenderer || !scene) return

    let cancelled = false

    // Wait a beat so the GLB has finished loading into the scene graph.
    const id = setTimeout(async () => {
      if (cancelled) return

      // Fit the bake to the ACTUAL terrain bounds rather than a hardcoded world size.
      //
      // The water plane is 2000 units across but the island spans only tens of units, so a
      // 2000-unit bake put the whole coastline inside a handful of texels — the distance field
      // was almost entirely empty sea and the foam band came out at the wrong scale.
      const measure = () => {
        const b = new THREE.Box3()
        let meshes = 0
        scene.traverse((o) => {
          // Skip the water plane itself (2000 units across) — including it would blow the
          // bounds out to the full ocean and defeat the point of fitting to the land.
          if (o.isMesh && !o.isWaterMesh) { b.expandByObject(o); meshes++ }
        })
        return { box: b, meshes }
      }

      // Wait for the terrain to actually BE there rather than trusting a fixed delay.
      //
      // A hardcoded timeout is a race: on a slower device (and mobile loads a different, larger
      // GLB) the model may not have arrived yet, so the bake measures an empty or partial scene
      // and the fitted bounds — and therefore the foam scale — come out wrong.
      let measured = measure()
      const deadline = Date.now() + 15000
      while (!cancelled && measured.meshes < 5 && Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 200))
        measured = measure()
      }
      if (cancelled) return

      const bounds = measured.box

      // Ignore the giant water plane itself when measuring, then pad so foam has room to fall off.
      const bsize = bounds.getSize(new THREE.Vector3())
      const bcentre = bounds.getCenter(new THREE.Vector3())
      const span = Math.min(Math.max(bsize.x, bsize.z), 2000)
      const bakeSize = span * 1.4 + maxDistance * 2
      const bakeCentre = { x: bcentre.x, z: bcentre.z }

      // 1. Render the terrain from above into a render target, unlit and white-on-black.
      const rt = new THREE.RenderTarget(resolution, resolution, {
        type: THREE.UnsignedByteType,
        format: THREE.RGBAFormat,
        depthBuffer: true,
      })

      const cam = new THREE.OrthographicCamera(
        -bakeSize / 2, bakeSize / 2, bakeSize / 2, -bakeSize / 2,
        0.1, 10000,
      )
      cam.position.set(bakeCentre.x, 500, bakeCentre.z)
      cam.lookAt(bakeCentre.x, 0, bakeCentre.z)
      cam.updateMatrixWorld()

      // Everything at or below the water plane is clipped away, so only land above the
      // waterline is captured — that is what defines the coastline.
      const clipPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -waterLevel)

      const flatWhite = new THREE.MeshBasicNodeMaterial({ color: 0xffffff })
      flatWhite.clippingPlanes = [clipPlane]
      flatWhite.clipping = true

      const previousOverride = scene.overrideMaterial
      const previousBackground = scene.background
      const previousClipping = gl.localClippingEnabled

      scene.overrideMaterial = flatWhite
      scene.background = new THREE.Color(0x000000)
      gl.localClippingEnabled = true

      // Render the top-down mask, then read it back so the distance transform can run on the CPU.
      const prevTarget = gl.getRenderTarget()
      gl.setRenderTarget(rt)
      gl.render(scene, cam)
      gl.setRenderTarget(prevTarget)

      let pixels
      try {
        // WebGPU exposes the async form; it returns the buffer rather than filling one.
        pixels = await gl.readRenderTargetPixelsAsync(rt, 0, 0, resolution, resolution)
      } catch (err) {
        // Restore scene state before bailing, or the whole scene stays flat white.
        scene.overrideMaterial = previousOverride
        scene.background = previousBackground
        gl.localClippingEnabled = previousClipping
        flatWhite.dispose()
        rt.dispose()
        throw err
      }

      scene.overrideMaterial = previousOverride
      scene.background = previousBackground
      gl.localClippingEnabled = previousClipping
      flatWhite.dispose()
      rt.dispose()

      if (cancelled) return

      // 2. Two-pass chamfer distance transform — cheap, and accurate enough for foam.
      //    Land texels start at 0; water texels propagate outwards.
      const n = resolution
      const dist = new Float32Array(n * n)
      const INF = 1e9

      for (let i = 0; i < n * n; i++) {
        dist[i] = pixels[i * 4] > 127 ? 0 : INF
      }

      // forward pass
      for (let y = 0; y < n; y++) {
        for (let x = 0; x < n; x++) {
          const i = y * n + x
          let d = dist[i]
          if (x > 0)             d = Math.min(d, dist[i - 1] + 1)
          if (y > 0)             d = Math.min(d, dist[i - n] + 1)
          if (x > 0 && y > 0)    d = Math.min(d, dist[i - n - 1] + 1.4142)
          if (x < n - 1 && y > 0) d = Math.min(d, dist[i - n + 1] + 1.4142)
          dist[i] = d
        }
      }
      // backward pass
      for (let y = n - 1; y >= 0; y--) {
        for (let x = n - 1; x >= 0; x--) {
          const i = y * n + x
          let d = dist[i]
          if (x < n - 1)              d = Math.min(d, dist[i + 1] + 1)
          if (y < n - 1)              d = Math.min(d, dist[i + n] + 1)
          if (x < n - 1 && y < n - 1) d = Math.min(d, dist[i + n + 1] + 1.4142)
          if (x > 0 && y < n - 1)     d = Math.min(d, dist[i + n - 1] + 1.4142)
          dist[i] = d
        }
      }

      // 3. Encode distance-from-land into a texture: 1 at the coast, falling to 0 at maxDistance.
      const worldPerTexel = bakeSize / n
      const out = new Uint8Array(n * n * 4)
      for (let i = 0; i < n * n; i++) {
        const worldDist = dist[i] * worldPerTexel
        const v = Math.max(0, 1 - worldDist / maxDistance)
        const b = Math.round(v * 255)
        out[i * 4] = b
        out[i * 4 + 1] = b
        out[i * 4 + 2] = b
        out[i * 4 + 3] = 255
      }

      const tex = new THREE.DataTexture(out, n, n, THREE.RGBAFormat)
      tex.wrapS = THREE.ClampToEdgeWrapping
      tex.wrapT = THREE.ClampToEdgeWrapping
      tex.minFilter = THREE.LinearFilter
      tex.magFilter = THREE.LinearFilter
      tex.needsUpdate = true

      // Render the field to a canvas — used for both the debug preview and the PNG export.
      const canvas = document.createElement('canvas')
      canvas.width = canvas.height = n
      const ctx = canvas.getContext('2d')
      // No row flip here.
      //
      // Determined by testing rather than reasoning: exporting the rows as-is and letting the
      // loader read the PNG with flipY = TRUE (TextureLoader's default) produces correctly
      // aligned foam. An earlier attempt flipped here AND disabled flipY on load, which is two
      // flips and lands back where it started.
      const img = ctx.createImageData(n, n)
      img.data.set(out)
      ctx.putImageData(img, 0, 0)

      // ?maskdebug=true pins the field to the top-right so it can be eyeballed:
      // white = coastline, fading to black offshore, black inland.
      if (new URLSearchParams(window.location.search).get('maskdebug') === 'true') {
        canvas.id = 'shoreline-mask-debug'
        document.body.appendChild(canvas)
      }

      // ?bakeshoreline=true downloads the PNG + metadata to drop into public/textures/.
      // This is the authoring step; normal loads read those files instead of baking.
      if (FORCE_BAKE) {
        canvas.toBlob((blob) => {
          const a = document.createElement('a')
          a.href = URL.createObjectURL(blob)
          a.download = 'shoreline-mask.png'
          a.click()
          URL.revokeObjectURL(a.href)
        }, 'image/png')

        const metaBlob = new Blob(
          [JSON.stringify({ size: bakeSize, centre: bakeCentre, maxDistance, resolution: n }, null, 2)],
          { type: 'application/json' },
        )
        const m = document.createElement('a')
        m.href = URL.createObjectURL(metaBlob)
        m.download = 'shoreline-mask.json'
        m.click()
        URL.revokeObjectURL(m.href)
      }

      if (!cancelled) setMask({ texture: tex, size: bakeSize, centre: bakeCentre, maxDistance })
    }, 1500)

    return () => {
      cancelled = true
      clearTimeout(id)
    }
  }, [gl, scene, size, resolution, waterLevel, maxDistance])

  // Note: no dispose-on-unmount here. The texture is either a shared loaded PNG or a small
  // DataTexture, and disposing on every dependency change tore down a texture still in use.

  return mask
}

// Re-baking
// ---------
// The field is baked from saxhq.glb, so it MUST be regenerated whenever the model's coastline
// changes — otherwise the foam silently drifts out of alignment with the land.
//
//   1. npm run dev
//   2. open http://localhost:5173/?bakeshoreline=true on a WebGPU-capable browser
//   3. two files download: shoreline-mask.png and shoreline-mask.json
//   4. move both into public/textures/
//
// If the baked files are missing the hook falls back to baking at runtime, so the foam still
// works — it just costs a render target, a GPU readback and a distance transform on every load.
